import React, { createContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

export const AuthContext = createContext();

// ERP-style model: ONLY these roles bypass the permission map entirely.
// Every other role (sales, lab, bio info, finance, bio IT, Custom) is driven by
// the EFFECTIVE permissions returned at login (role defaults already merged with extras).
export const FULL_ACCESS_ROLES = ['Super admin', 'Admin'];

const applyAuthHeader = (token) => {
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    else delete axios.defaults.headers.common['Authorization'];
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                if (decoded.exp * 1000 < Date.now()) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('permissions');
                    applyAuthHeader(null);
                } else {
                    applyAuthHeader(token);
                    setUser({ id: decoded.id, username: decoded.username, role: decoded.role });
                    try { setPermissions(JSON.parse(localStorage.getItem('permissions') || '{}')); }
                    catch { setPermissions({}); }
                }
            } catch (err) {
                console.error('Token decode failed:', err);
                localStorage.removeItem('token');
                localStorage.removeItem('permissions');
                applyAuthHeader(null);
            }
        }
        setLoading(false);
    }, []);

    // Called from Login after POST /auth/login -> data = { token, permissions, user, mustChangePassword }
    const login = useCallback((token, perms = {}) => {
        localStorage.setItem('token', token);
        localStorage.setItem('permissions', JSON.stringify(perms || {}));
        applyAuthHeader(token);
        const decoded = jwtDecode(token);
        setUser({ id: decoded.id, username: decoded.username, role: decoded.role });
        setPermissions(perms || {});
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('permissions');
        applyAuthHeader(null);
        setUser(null);
        setPermissions({});
        window.location.href = '/';
    }, []);

    // Effective permission check. Admin/Super bypass; everyone else uses the map.
    const can = useCallback((moduleKey, action = 'view') => {
        if (!user) return false;
        if (FULL_ACCESS_ROLES.includes(user.role)) return true;
        let perms = permissions;
        if (!perms || Object.keys(perms).length === 0) {
            try { perms = JSON.parse(localStorage.getItem('permissions') || '{}'); }
            catch { perms = {}; }
        }
        const p = perms[moduleKey];
        return !!(p && p[action]);
    }, [user, permissions]);

    const canView = useCallback((moduleKey) => can(moduleKey, 'view'), [can]);

    if (loading) return <div>Loading...</div>;

    return (
        <AuthContext.Provider value={{ user, permissions, login, logout, can, canView }}>
            {children}
        </AuthContext.Provider>
    );
};